import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  BrainCircuit,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  Sparkles,
  Hand,
  Settings2,
  Upload,
  AlertTriangle,
  Tag,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import connectToSocket from "../../services/socket-io";

const CATEGORIAS = [
  { value: "attention", label: "Atenção", emoji: "⚠️", desc: "Alertas, restrições, cuidado especial" },
  { value: "commercial", label: "Comercial", emoji: "💼", desc: "Preços, contratos, negociação" },
  { value: "preferences", label: "Preferências", emoji: "💖", desc: "Coisas que gosta / não gosta" },
  { value: "history", label: "Histórico", emoji: "📜", desc: "Anedotas, relacionamento passado" },
  { value: "profile", label: "Perfil", emoji: "👤", desc: "Dados pessoais relevantes" },
  { value: "notes", label: "Observações", emoji: "📝", desc: "Geral" },
];

const CATEGORIA_POR_VALOR = Object.fromEntries(CATEGORIAS.map((c) => [c.value, c]));

const FONTE_POR_VALOR = {
  manual: { label: "Manual", emoji: "🖐️", Icon: Hand },
  ai: { label: "IA (sugerida)", emoji: "🤖", Icon: Sparkles },
  system: { label: "Sistema", emoji: "🔧", Icon: Settings2 },
  import: { label: "Importado", emoji: "⬆️", Icon: Upload },
};

const STATUS_POR_VALOR = {
  confirmed: { label: "Confirmada", cor: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200/50", Icone: CheckCircle2 },
  pending: { label: "Sugestão", cor: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200/50", Icone: AlertTriangle },
  rejected: { label: "Rejeitada", cor: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-200/50", Icone: XCircle },
};

const MEMORY_VAZIA = {
  id: null,
  category: "notes",
  title: "",
  content: "",
  source: "manual",
};

const CustomerMemoryPanel = ({ contactId }) => {
  const [memorias, setMemorias] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [sending, setSending] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState("todas");

  const [dialogAberto, setDialogAberto] = useState(false);
  const [memoryForm, setMemoryForm] = useState(MEMORY_VAZIA);
  const [modoEdicao, setModoEdicao] = useState(false);

  const carregar = useCallback(async () => {
    if (!contactId) return;
    try {
      setCarregando(true);
      const { data } = await api.get(`/customer-memory/contact/${contactId}`);
      const linhas = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
      setMemorias(linhas);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, [contactId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Socket listener para atualizar em tempo real
  useEffect(() => {
    const socket = connectToSocket();
    const onUpdate = (payload) => {
      if (!payload) return;
      // Se payload.contactId bater, recarrega
      const ids = [].concat(payload.contactId || []).concat(payload.contactIds || []);
      if (ids.length === 0 || ids.includes(Number(contactId)) || ids.includes(String(contactId))) {
        carregar();
      }
    };
    socket.on("customerMemory:update", onUpdate);
    return () => {
      socket.off("customerMemory:update", onUpdate);
      socket.disconnect();
    };
  }, [carregar, contactId]);

  const grupos = useMemo(() => {
    const filtradas =
      filtroCategoria === "todas"
        ? memorias
        : memorias.filter((m) => m.category === filtroCategoria);

    const porCat = CATEGORIAS.reduce((acc, c) => {
      acc[c.value] = filtradas.filter((m) => m.category === c.value);
      return acc;
    }, {});
    return CATEGORIAS.map((c) => ({
      ...c,
      itens: porCat[c.value] || [],
    })).filter((g) => g.itens.length > 0);
  }, [memorias, filtroCategoria]);

  const total = memorias.length;
  const pendentes = memorias.filter((m) => m.status === "pending").length;

  const abrirCriar = () => {
    setMemoryForm({ ...MEMORY_VAZIA });
    setModoEdicao(false);
    setDialogAberto(true);
  };

  const abrirEditar = (m) => {
    setMemoryForm({
      id: m.id,
      category: m.category,
      title: m.title || "",
      content: m.content || "",
      source: m.source || "manual",
    });
    setModoEdicao(true);
    setDialogAberto(true);
  };

  const salvar = async () => {
    try {
      setSending(true);
      const payload = {
        contactId: Number(contactId),
        category: memoryForm.category,
        title: memoryForm.title.trim() || null,
        content: memoryForm.content.trim(),
        source: modoEdicao ? memoryForm.source : "manual",
        status: modoEdicao ? undefined : "confirmed",
      };
      if (!payload.content) {
        toastError(new Error("Conteúdo é obrigatório."));
        return;
      }
      if (modoEdicao && memoryForm.id) {
        await api.put(`/customer-memory/${memoryForm.id}`, payload);
      } else {
        await api.post(`/customer-memory`, payload);
      }
      setDialogAberto(false);
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const remover = async (id) => {
    if (!window.confirm("Remover esta memória permanentemente?")) return;
    try {
      await api.delete(`/customer-memory/${id}`);
      carregar();
    } catch (err) {
      toastError(err);
    }
  };

  const confirmar = async (id) => {
    try {
      await api.post(`/customer-memory/${id}/confirm`);
      carregar();
    } catch (err) {
      toastError(err);
    }
  };

  const rejeitar = async (id) => {
    try {
      await api.post(`/customer-memory/${id}/reject`);
      carregar();
    } catch (err) {
      toastError(err);
    }
  };

  if (!carregando && total === 0) {
    return (
      <Card className="mx-4 mb-4 mt-4 sm:mx-6">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <BrainCircuit className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-base">Memória do cliente</CardTitle>
              <CardDescription>
                Registro inteligente do que importa para este cliente.
              </CardDescription>
            </div>
          </div>
          <Button size="sm" onClick={abrirCriar}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Nova memória
          </Button>
        </CardHeader>
        <CardContent>
          <p className="py-6 text-center text-xs text-muted-foreground">
            Nenhuma memória registrada ainda. Clique em "Nova memória" para começar.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mx-4 mb-4 mt-4 sm:mx-6">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <BrainCircuit className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                Memória do cliente
                {carregando && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </CardTitle>
              <CardDescription className="flex flex-wrap items-center gap-2">
                {total} registro(s)
                {pendentes > 0 && (
                  <Badge variant="outline" className="h-5 border-amber-200/60 bg-amber-50 px-2 py-0 text-[10px] font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                    ⚠️ {pendentes} sugestão(ões) pendente(s)
                  </Badge>
                )}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="h-8 w-[150px]">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {CATEGORIAS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.emoji} {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={abrirCriar}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              Nova
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {grupos.length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">
              Nenhuma memória nesta categoria.
            </p>
          )}
          {grupos.map((g) => (
            <section key={g.value}>
              <div className="mb-1.5 flex items-center gap-1.5">
                <span className="text-sm">{g.emoji}</span>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {g.label}
                </h3>
                <Badge variant="secondary" className="h-4 rounded-full px-1.5 py-0 text-[10px] font-normal">
                  {g.itens.length}
                </Badge>
              </div>
              <ul className="space-y-2">
                {g.itens.map((m) => {
                  const fonte = FONTE_POR_VALOR[m.source] || FONTE_POR_VALOR.manual;
                  const status = STATUS_POR_VALOR[m.status] || STATUS_POR_VALOR.confirmed;
                  const StatusIcone = status.Icone;
                  return (
                    <li
                      key={m.id}
                      className={cn(
                        "group rounded-lg border p-3 transition-colors",
                        m.status === "rejected" && "opacity-60",
                        m.status === "pending" && "border-amber-200/60 bg-amber-50/30 dark:bg-amber-500/5"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {m.title && (
                              <h4 className="truncate text-sm font-semibold">
                                {m.title}
                              </h4>
                            )}
                            <Badge variant="outline" className={cn("h-5 rounded-full border px-2 py-0 text-[10px] font-medium", status.cor)}>
                              <StatusIcone className="mr-1 h-3 w-3 inline -mt-0.5" />
                              {status.label}
                            </Badge>
                            <Badge variant="secondary" className="h-5 rounded-full px-2 py-0 text-[10px]">
                              {fonte.emoji} {fonte.label}
                            </Badge>
                            {typeof m.confidence === "number" && !Number.isNaN(m.confidence) && (
                              <Badge variant="outline" className="h-5 rounded-full px-2 py-0 text-[10px]">
                                <Tag className="mr-1 h-3 w-3 inline -mt-0.5" />
                                {Math.round(Number(m.confidence) * 100)}%
                              </Badge>
                            )}
                            {m.createdAt && (
                              <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground">
                                {format(parseISO(m.createdAt), "dd/MM HH:mm")}
                              </span>
                            )}
                          </div>
                          <p className="whitespace-pre-line text-xs leading-relaxed text-foreground/90">
                            {m.content}
                          </p>
                          {(m.createdBy?.name || m.updatedBy?.name) && (
                            <p className="text-[10px] text-muted-foreground">
                              {m.updatedBy?.name || m.createdBy?.name}
                              {m.updatedBy?.name && " (editado)"}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          {m.status === "pending" && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-emerald-700 dark:text-emerald-400"
                                title="Confirmar"
                                onClick={() => confirmar(m.id)}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-rose-700 dark:text-rose-400"
                                title="Rejeitar"
                                onClick={() => rejeitar(m.id)}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => abrirEditar(m)}>
                                <Pencil className="mr-2 h-3.5 w-3.5" />
                                Editar
                              </DropdownMenuItem>
                              {m.status !== "pending" && m.status !== "confirmed" && (
                                <DropdownMenuItem onClick={() => confirmar(m.id)}>
                                  <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
                                  Marcar confirmada
                                </DropdownMenuItem>
                              )}
                              {m.status === "pending" && (
                                <>
                                  <DropdownMenuItem onClick={() => confirmar(m.id)}>
                                    <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
                                    Confirmar
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => rejeitar(m.id)}>
                                    <XCircle className="mr-2 h-3.5 w-3.5" />
                                    Rejeitar
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-rose-700 dark:text-rose-400"
                                onClick={() => remover(m.id)}
                              >
                                <Trash2 className="mr-2 h-3.5 w-3.5" />
                                Remover
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </CardContent>
      </Card>

      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {modoEdicao ? "Editar memória" : "Nova memória do cliente"}
            </DialogTitle>
            <DialogDescription>
              Esta informação ficará disponível nos atendimentos deste cliente,
              nos copilot e nas perguntas do Perabit Intelligence.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="sm:col-span-2">
              <Label>Categoria</Label>
              <Select
                value={memoryForm.category}
                onValueChange={(v) => setMemoryForm((s) => ({ ...s, category: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.emoji} {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {CATEGORIA_POR_VALOR[memoryForm.category]?.desc}
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>Título (opcional)</Label>
              <Input
                className="mt-1"
                placeholder="Ex: Alergia a amendoim"
                value={memoryForm.title}
                onChange={(e) => setMemoryForm((s) => ({ ...s, title: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-4">
              <Label>Conteúdo</Label>
              <Textarea
                className="mt-1 min-h-[110px]"
                placeholder="Detalhe o que é importante lembrar sobre este cliente."
                value={memoryForm.content}
                onChange={(e) => setMemoryForm((s) => ({ ...s, content: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogAberto(false)}
              disabled={sending}
            >
              Cancelar
            </Button>
            <Button onClick={salvar} disabled={sending}>
              {sending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              {modoEdicao ? "Salvar alterações" : "Criar memória"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerMemoryPanel;
